import { BRAND_TYPE, } from '@/enums/configs';

const LOGIN_SDK_TIMEOUT = 5_000;
const LOGIN_SDK_ATTRIBUTE = 'data-login-sdk';

interface LoginSDKOptions {
  header: HTMLElement;
  footer: HTMLElement;
  env: 'test' | 'stg' | 'prod';
  onLogin: (user: unknown) => void;
  onSDKLogin: (user: unknown) => void;
}

export interface LoginSDKInstance {
  getUserInfo?: () => unknown | Promise<unknown>;
}

export type LoginSDKConstructor = new (
  options: LoginSDKOptions
) => LoginSDKInstance;

const LOGIN_SDK_CONFIG: Record<
  BRAND_TYPE,
  { src: string; globalName: string }
> = {
  [BRAND_TYPE.NIO]: {
    src: 'https://cdn-public.nio.com/www-nio-cn/sdk/cdn-static/nio-navigation-sdk.js',
    globalName: 'NIONavigationSDK',
  },
  [BRAND_TYPE.ONVO]: {
    src: 'https://cdn-public.nio.com/www-alps-cn/cms/cdn-static/alps-navigation-sdk.js',
    globalName: 'AlpsNavigationSDK',
  },
  [BRAND_TYPE.FY]: {
    src: 'https://cdn-public.nio.com/www-firefly-cn/cms/cdn-static/firefly-navigation-sdk.js',
    globalName: 'FireflyNavigationSDK',
  },
};

let loginSDKPromise: Promise<LoginSDKConstructor> | null = null;

const getLoginSDKConstructor = (globalName: string) => {
  const loginSDKConstructor
    = (window as unknown as Record<string, unknown>)[globalName];

  return typeof loginSDKConstructor === 'function'
    ? loginSDKConstructor as LoginSDKConstructor
    : null;
};

export const loadLoginSDK = (
  timeout = LOGIN_SDK_TIMEOUT
): Promise<LoginSDKConstructor> => {
  const brand = process.env.BRAND as BRAND_TYPE;
  const loginSDKBrand = LOGIN_SDK_CONFIG[brand] ? brand : BRAND_TYPE.NIO;
  const loginSDKConfig = LOGIN_SDK_CONFIG[loginSDKBrand];
  const loadedLoginSDKConstructor
    = getLoginSDKConstructor(loginSDKConfig.globalName);

  if (loadedLoginSDKConstructor) {
    return Promise.resolve(loadedLoginSDKConstructor);
  }

  if (loginSDKPromise) {
    return loginSDKPromise;
  }

  loginSDKPromise = new Promise<LoginSDKConstructor>((resolve, reject) => {
    const script = document.createElement('script');
    let settled = false;
    let timer = 0;

    script.async = true;
    script.src = loginSDKConfig.src;
    script.setAttribute(LOGIN_SDK_ATTRIBUTE, loginSDKBrand);

    const cleanup = () => {
      window.clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    };
    const fail = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      script.remove();
      reject(error);
    };

    timer = window.setTimeout(() => {
      fail(new Error(`loginSDK load timed out after ${timeout}ms`));
    }, timeout);

    script.onload = () => {
      if (settled) {
        return;
      }

      const loginSDKConstructor
        = getLoginSDKConstructor(loginSDKConfig.globalName);

      if (!loginSDKConstructor) {
        fail(new Error(`loginSDK global ${loginSDKConfig.globalName} is invalid`));
        return;
      }

      settled = true;
      cleanup();
      resolve(loginSDKConstructor);
    };
    script.onerror = () => {
      fail(new Error(`Failed to load loginSDK: ${loginSDKConfig.src}`));
    };

    document.head.appendChild(script);
  }).catch((error) => {
    loginSDKPromise = null;
    throw error;
  });

  return loginSDKPromise;
};
